import { useEffect, useRef, useState } from "react";
import { IFood } from "../models/IFood";
import { S3_IMAGE_BASE_URL } from "../services/config";
import FoodDetailPopup from "../popupPages/FoodDetailPopup";
import MobileFoodDetailPopup from "../popupPages/MobileFoodDetailPopup";
import ProfileIncompletePopup from "../popupPages/ProfileIncompletePopup";
import { fetchVerificationStatus, isProfileComplete } from "../utils/verificationCache";
import { isLoggedIn } from "../utils/tokenUtils";
import { useLanguage } from "../context/LanguageContext";

interface Props {
  foods: IFood[];
  minAmount: number;
  disabled?: boolean;
}

function FoodList({ foods, minAmount, disabled = false }: Props) {
  const { t, lang } = useLanguage();
  const [selectedFood, setSelectedFood] = useState<IFood | null>(null);
  const [activeCategory, setActiveCategory] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const profileTriggerRef = useRef<HTMLButtonElement>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const handleAddFood = async (food: IFood, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (disabled) return;
    if (isLoggedIn()) {
      await fetchVerificationStatus();
      if (!isProfileComplete()) {
        profileTriggerRef.current?.click();
        return;
      }
    }
    setSelectedFood(food);
  };

  const isMobile = () => window.innerWidth < 576;

  useEffect(() => {
    if (selectedFood && !isMobile()) {
      triggerRef.current?.click();
    }
  }, [selectedFood]);

  useEffect(() => {
    const modalEl = document.getElementById("foodDetailModal");
    if (modalEl) {
      const handleHide = () => setSelectedFood(null);
      modalEl.addEventListener("hidden.bs.modal", handleHide);
      return () => modalEl.removeEventListener("hidden.bs.modal", handleHide);
    }
  }, []);

  // Kategoriye göre grupla — priority'ye göre sırala (0 en başta)
  const grouped: { categoryName: string; priority: number; items: IFood[] }[] = [];
  const categoryIndex: Record<string, number> = {};
  for (const food of foods) {
    const cat = (lang === "en" ? food.foodCategory?.foodCategoryNameEn : food.foodCategory?.foodCategoryNameTr) ?? t("food.other");
    if (categoryIndex[cat] === undefined) {
      categoryIndex[cat] = grouped.length;
      grouped.push({ categoryName: cat, priority: food.foodCategory?.priority ?? 999, items: [] });
    }
    grouped[categoryIndex[cat]].items.push(food);
  }
  grouped.sort((a, b) => a.priority - b.priority);

  useEffect(() => {
    if (grouped.length > 0 && !activeCategory) {
      setActiveCategory(grouped[0].categoryName);
    }
  }, [grouped.length]);

  const scrollToCategory = (cat: string) => {
    setActiveCategory(cat);
    sectionRefs.current[cat]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* Kategori tabları */}
      {grouped.length > 1 && (
        <div
          className="mb-4 pb-1"
          style={{ overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" } as React.CSSProperties}
        >
          <div className="d-flex gap-2" style={{ whiteSpace: "nowrap" }}>
            {grouped.map(({ categoryName, items }) => (
              <button
                key={categoryName}
                className={`btn btn-sm rounded-pill px-3 fw-semibold${activeCategory === categoryName ? " btn-danger" : " btn-outline-secondary"}`}
                style={{ flexShrink: 0 }}
                onClick={() => scrollToCategory(categoryName)}
              >
                {categoryName}
                <span
                  className={`ms-1 badge rounded-pill${activeCategory === categoryName ? " bg-white text-danger" : " bg-secondary"}`}
                  style={{ fontSize: "0.65rem" }}
                >
                  {items.length}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="d-flex flex-column gap-4">
        {grouped.map(({ categoryName, items }) => (
          <div key={categoryName} ref={(el) => { sectionRefs.current[categoryName] = el; }}>
            <h6
              className="fw-bold mb-3 px-1"
              style={{ borderLeft: "4px solid #c20e1a", paddingLeft: "10px" }}
            >
              {categoryName}
            </h6>
            <div className="row g-3">
              {items.map((food) => (
                <div key={food.id} className="col-12 col-md-6 col-lg-4">
                  {/* overflow-hidden YOK — "+" butonu kesilmesin; resim kendi div'inde yuvarlatılıyor */}
                  <div
                    className="food-card bg-white shadow-sm position-relative"
                    style={{
                      cursor: disabled ? "default" : "pointer",
                      border: "1px solid #f0f0f0",
                      borderRadius: "1rem",
                    }}
                    onClick={() => handleAddFood(food)}
                  >
                    <div className="d-flex" style={{ minHeight: "110px" }}>
                      {/* Sol içerik */}
                      <div className="p-3 d-flex flex-column justify-content-between flex-grow-1" style={{ minWidth: 0 }}>
                        <span className="fw-semibold text-dark" style={{ fontSize: "0.9rem", lineHeight: "1.3" }}>
                          {lang === "en" ? food.foodNameEn : food.foodNameTr}
                        </span>
                        {food.foodContents && (
                          <small
                            className="text-muted mt-1"
                            style={{
                              fontSize: "0.73rem",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {food.foodContents}
                          </small>
                        )}
                        {food.hasDiscount && food.discountedPriceText ? (
                          <span className="fw-bold mt-2 d-flex align-items-center flex-wrap gap-1" style={{ fontSize: "0.95rem" }}>
                            <s className="text-muted fw-normal" style={{ fontSize: "0.75rem" }}>
                              {food.foodPriceText} {food.currency.currencyDisplayText}
                            </s>
                            <span style={{ color: "#c20e1a" }}>
                              {food.discountedPriceText} {food.currency.currencyDisplayText}
                            </span>
                            <span className="badge bg-warning text-dark" style={{ fontSize: "0.6rem" }}>
                              <i className="bi bi-percent"></i>
                            </span>
                          </span>
                        ) : (
                          <span className="fw-bold mt-2" style={{ color: "#c20e1a", fontSize: "0.95rem" }}>
                            {food.foodPriceText} {food.currency.currencyDisplayText}
                          </span>
                        )}
                      </div>

                      {/* Sağ resim — kendi overflow'u ile yuvarlatılmış */}
                      <div
                        className="flex-shrink-0"
                        style={{ width: "120px", overflow: "hidden", borderRadius: "0 1rem 1rem 0" }}
                      >
                        <img
                          src={S3_IMAGE_BASE_URL + food.foodImagePath}
                          alt={lang === "en" ? food.foodNameEn : food.foodNameTr}
                          style={{ width: "120px", height: "110px", objectFit: "cover", display: "block" }}
                        />
                      </div>
                    </div>

                    {/* + butonu — kart üzerinde, image'ın sağ altında */}
                    {!disabled && (
                      <button
                        className="position-absolute d-flex align-items-center justify-content-center border-0"
                        style={{
                          bottom: "8px",
                          right: "8px",
                          width: "30px",
                          height: "30px",
                          background: "#c20e1a",
                          borderRadius: "50%",
                          color: "white",
                          fontSize: "1.3rem",
                          lineHeight: 1,
                          padding: 0,
                          zIndex: 3,
                          boxShadow: "0 2px 8px rgba(194,14,26,0.45)",
                        }}
                        onClick={(e) => handleAddFood(food, e)}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Gizli modal tetikleyiciler */}
      <button ref={triggerRef} data-bs-toggle="modal" data-bs-target="#foodDetailModal" className="d-none" />
      <button ref={profileTriggerRef} data-bs-toggle="modal" data-bs-target="#profileIncompleteModal" className="d-none" />

      <FoodDetailPopup food={selectedFood} minAmount={minAmount} />
      {selectedFood && isMobile() && (
        <MobileFoodDetailPopup
          food={selectedFood}
          minAmount={minAmount}
          onClose={() => setSelectedFood(null)}
        />
      )}
      <ProfileIncompletePopup />
    </>
  );
}

export default FoodList;
